import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tenantAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Store, MapPin, ChevronRight, LogOut, Plus, LayoutDashboard, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { BentaWordmark } from '../components/BentaLogo';

const SelectOutletPage = () => {
  const navigate = useNavigate();
  const { user, logout, selectOutlet, isOwner, isManager, isCashier } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOutlets();
  }, []);

  const fetchOutlets = async () => {
    try {
      const response = await tenantAPI.getOutlets();
      const list = response.data || [];
      setOutlets(list);
      // P2.8: with a single outlet, skip the selector and go straight in.
      if (list.length === 1) {
        handleSelectOutlet(list[0]);
        return;
      }
    } catch (error) {
      toast.error('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOutlet = (outlet, goToPOS = false) => {
    selectOutlet(outlet);
    toast.success(`Selected ${outlet.name}`);
    
    // Route based on role and intent
    if (goToPOS || isCashier) {
      navigate('/pos');
    } else if (isOwner || isManager) {
      navigate('/dashboard');
    } else {
      navigate('/pos');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0f1115] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <BentaWordmark className="text-2xl text-white mb-1" accentClassName="text-teal-400" />
            <h1 className="font-heading text-3xl font-bold text-white">Select Outlet</h1>
            <p className="text-gray-400 mt-1">Welcome back, {user?.name}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-gray-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Outlets Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-[#181b21] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : outlets.length === 0 ? (
          <Card className="bg-[#181b21] border-white/10">
            <CardContent className="py-12 text-center">
              <Store className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Outlets Found</h3>
              <p className="text-gray-400 mb-4">Create your first outlet to get started</p>
              {isOwner && (
                <Button
                  className="bg-[#29a38f] hover:bg-[#228b7a]"
                  onClick={() => navigate('/dashboard/outlets')}
                  data-testid="create-outlet-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Outlet
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {outlets.map((outlet) => (
              <Card
                key={outlet.id}
                className="bg-[#181b21] border-white/10 hover:border-[#29a38f]/50 cursor-pointer transition-all duration-200 group"
                onClick={() => handleSelectOutlet(outlet)}
                data-testid={`outlet-card-${outlet.code}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-[#29a38f]/20 flex items-center justify-center">
                      <Store className="w-6 h-6 text-[#29a38f]" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-[#29a38f] transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">{outlet.name}</h3>
                  <p className="text-sm text-gray-400 mb-4 truncate">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {outlet.address || 'No address'}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="bg-[#20242c] px-2 py-1 rounded">{outlet.code}</span>
                    <span className="capitalize">{outlet.service_mode?.replace('_', ' ')}</span>
                  </div>
                  
                  {/* Quick action buttons for owner/manager */}
                  {(isOwner || isManager) && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-white/10 text-gray-400 hover:text-white text-xs"
                        onClick={(e) => { e.stopPropagation(); handleSelectOutlet(outlet, false); }}
                        data-testid={`dashboard-btn-${outlet.code}`}
                      >
                        <LayoutDashboard className="w-3 h-3 mr-1" />
                        Dashboard
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-[#29a38f] hover:bg-[#228b7a] text-xs"
                        onClick={(e) => { e.stopPropagation(); handleSelectOutlet(outlet, true); }}
                        data-testid={`pos-btn-${outlet.code}`}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        POS
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectOutletPage;
